/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { AboutInfo } from '../../../models/about-info';
import {
  ABOUT_DIALOG_FACADE_TOKEN,
  IAboutDialogFacade,
} from '../../../../../../data-access/src/lib/domain/facades-models/about-dialog-facade-interface';
import { take } from 'rxjs';
import { AcrylicSurfaceService } from '../../../acrylic-surface/acrylic-surface.service';

@Component({
  selector: 'sysque-about-dialog',
  templateUrl: './about-dialog.component.html',
  styleUrls: ['./about-dialog.component.scss'],
  standalone: false
})

/**
 * Represents the About Dialog component.
 * This component displays information about the application and allows the user to close the dialog.
 *
 * @remarks
 * The About Dialog component implements the `OnInit` and `OnDestroy` lifecycle hooks.
 * It has properties for the component title, data, close event, show close button, user email, and version.
 * The component initializes the data and retrieves initial data on initialization.
 * It also handles the close event and opens URLs.
 */
export class AboutDialogComponent implements OnInit, OnDestroy {
  public componentTitle = '';
  public data: AboutInfo = {
    companyName: '',
    copyrights: '',
    productName: '',
  };

  @Output() close = new EventEmitter();
  public showCloseButton = false;
  public userEmail: string = '';
  public version: string = '';
  public supportContact = "my.trimble.com/contactsupport";

  constructor(@Inject(ABOUT_DIALOG_FACADE_TOKEN) private aboutDialogFacade: IAboutDialogFacade, private acrylicSurfaceService: AcrylicSurfaceService) {}
  
  /**
   * Initializes the component.
   * - Sets the company name to 'Trimble Inc.'
   * - Sets the copyrights to '© Copyright 2025, Trimble Inc.'
   * - Sets the product name to 'SysQue Electrical'
   * - Calls the 'getInitialData' method
   */
  public ngOnInit(): void {
    this.data.companyName = 'Trimble Inc.';
    this.data.copyrights = '© Copyright 2025, Trimble Inc.';
    this.data.productName = 'SysQue Electrical';
    this.getInitialData();
  }

  /**
   * Lifecycle hook that is called when the component is about to be destroyed.
   * It is used to perform any necessary cleanup before the component is removed from the DOM.
   */
  public ngOnDestroy(): void {
    this.acrylicSurfaceService.hide();
  } 

  /**
   * Closes the window.
   * 
   * This method calls the `closeWindow` method of the `aboutDialogFacade` to close the about dialog window.
   * It also emits the `close` event and hides the acrylic surface using the `acrylicSurfaceService`.
   */
  public closeWindow(): void  {
    this.aboutDialogFacade.closeWindow();
    this.close.emit();
    this.acrylicSurfaceService.hide();
  }

  /**
   * Retrieves initial data for the about dialog.
   */
  public getInitialData(): void  {
    this.aboutDialogFacade
      .getUserData('OnAboutDialogClick')
      .pipe(take(1))
      .subscribe((response) => {
        if (response !== null) {
          this.userEmail = response.item1;
          this.version = response.item2;
        }
      });
  }

  /**
   * Opens the specified URL.
   * 
   * @param url - The URL to open.
   */
  public openUrl(url: string): void {
    this.aboutDialogFacade.openUrl(url).pipe(take(1)).subscribe({
      error: (err) => {
        console.error('Error:', err);
      },
    });
  }
}
